import { useState } from "react";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormGrid,
  FormSection,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import type { ImportType } from "../../api/aiDataEntryApi";
import { toClientApiError } from "../../api/httpClient";
import { DataEntryRecommendation } from "../../next/ai/dataEntry/DataEntryRecommendation";

export type FileImportPreviewBase = {
  totalRows: number;
  estimatedCreate: number;
  estimatedSkip: number;
  unmappedHeaders: string[];
  errors: Array<{ line: number; message: string }>;
  sample: Array<Record<string, unknown>>;
};

type CashFileImportDialogProps<TPreview extends FileImportPreviewBase> = {
  open: boolean;
  onClose: () => void;
  title: string;
  hint: string;
  sampleColumns: Array<{ key: string; label: string }>;
  onPreview: (file: File) => Promise<TPreview>;
  onCommit: (preview: TPreview) => Promise<{ count: number } | { created: number }>;
  onDone: () => void;
  /** «מומלץ — הזנה חכמה» on the first step: the kind the agent opens with (ADR-0037) */
  recommendImportType?: ImportType;
};

export function CashFileImportDialog<TPreview extends FileImportPreviewBase>({
  open,
  onClose,
  title,
  hint,
  sampleColumns,
  onPreview,
  onCommit,
  onDone,
  recommendImportType,
}: CashFileImportDialogProps<TPreview>) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<TPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const reset = () => {
    setFileName(null);
    setPreview(null);
    setError(null);
    setBusy(false);
    setResult(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setFileName(file.name);
    try {
      setPreview(await onPreview(file));
    } catch (err) {
      setPreview(null);
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!preview || preview.estimatedCreate === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await onCommit(preview);
      const count = "count" in res ? res.count : res.created;
      setResult(`יובאו ${count} רשומות בהצלחה.`);
      onDone();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog open={open} onClose={handleClose} size="lg">
      <FormCard
        title={title}
        error={error}
        success={result}
        onSave={() => {
          void handleCommit();
        }}
        onCancel={handleClose}
        saveLabel="אישור ייבוא"
        cancelLabel="סגירה"
        saving={busy}
        saveDisabled={!preview || preview.estimatedCreate === 0 || busy || Boolean(result)}
      >
        <FormSection>
          <ol className="import-wizard-steps" aria-label="שלבי ייבוא">
            <li className={!preview ? "import-wizard-steps__active" : undefined}>
              1. העלאה
            </li>
            <li className={preview && !result ? "import-wizard-steps__active" : undefined}>
              2. תצוגה מקדימה
            </li>
            <li className={result ? "import-wizard-steps__active" : undefined}>
              3. אישור
            </li>
          </ol>
          <FormGrid cols={1}>
            {recommendImportType && !preview && !result ? <DataEntryRecommendation importType={recommendImportType} onLeave={handleClose} /> : null}
            <label className="file-import-picker">
              <span className="file-import-picker__label">בחירת קובץ Excel או CSV</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                disabled={busy}
                onChange={(event) => {
                  void handleFile(event.target.files);
                  event.target.value = "";
                }}
              />
              <span className="file-import-picker__hint">
                {hint}
                {fileName ? ` · נבחר: ${fileName}` : ""}
              </span>
            </label>

            {preview ? (
              <>
                <FormAlert severity="info">
                  {preview.totalRows} שורות בקובץ · צפויים להיווצר{" "}
                  {preview.estimatedCreate} · לדלג {preview.estimatedSkip}
                  {preview.unmappedHeaders.length > 0
                    ? ` · עמודות שלא מופו: ${preview.unmappedHeaders.join(", ")}`
                    : ""}
                </FormAlert>
                {preview.errors.length > 0 ? (
                  <FormAlert severity="warning">
                    שגיאות לדוגמה:{" "}
                    {preview.errors
                      .slice(0, 8)
                      .map((e) => `שורה ${e.line}: ${e.message}`)
                      .join(" · ")}
                  </FormAlert>
                ) : null}
                {preview.sample.length > 0 ? (
                  <div className="file-import-preview">
                    <table>
                      <thead>
                        <tr>
                          {sampleColumns.map((col) => (
                            <th key={col.key}>{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sample.slice(0, 10).map((row, idx) => (
                          <tr key={idx}>
                            {sampleColumns.map((col) => (
                              <td key={col.key}>{String(row[col.key] ?? "—")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </>
            ) : null}
          </FormGrid>
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}
